import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import './quill-bootstrap.css'
import './ql-container.css'
import classNames from '../../util/classNames'
import { useRef, useImperativeHandle, forwardRef, useEffect } from 'react'

const toolbarTitles: Record<string, string> = {
  '.ql-bold': 'Bold',
  '.ql-italic': 'Italic',
  '.ql-underline': 'Underline',
  '.ql-strike': 'Strikethrough',
  '.ql-blockquote': 'Block Quote',
  '.ql-list[value="ordered"]': 'Numbered List',
  '.ql-list[value="bullet"]': 'Bullet List',
  '.ql-indent[value="-1"]': 'Decrease Indent',
  '.ql-indent[value="+1"]': 'Increase Indent',
  '.ql-color': 'Text Color',
  '.ql-background': 'Background Color',
  '.ql-link': 'Insert Link',
  '.ql-image': 'Insert Image',
  '.ql-clean': 'Clear Formatting',
  '.ql-header': 'Heading',
}

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline','strike', 'blockquote'],
    [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
    [{ 'color': [] }, { 'background': [] }],
    ['link', 'image'],
    ['clean']
  ],
}

const formats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'blockquote',
  'list', 'indent',
  'color', 'background',
  'link', 'image'
]

interface Props {
  value?: string
  onChange?: (v:string)=>void
  className?: string
  defaultValue?:string
  readOnly?:boolean
}

const MessageEditor = forwardRef<{ getText: () => string }, Props>((props, ref) => {
  const { value, onChange, className, defaultValue, readOnly = false } = props;
  const quillRef = useRef<ReactQuill | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  useImperativeHandle(ref, () => ({
    getText: () => (quillRef.current?.getEditor().getText()?.trim() || ''),
  }))

  useEffect(() => {
    const wrapper = wrapperRef.current
    if (!wrapper) return
    for (const [selector, title] of Object.entries(toolbarTitles)) {
      wrapper.querySelectorAll<HTMLElement>(selector).forEach(el => {
        el.setAttribute('title', title)
      })
    }
  }, [])

  return (
    <div ref={wrapperRef} className={classNames('ql-wrapper', className)}>
      <ReactQuill
        ref={quillRef}
        theme='snow'
        value={value}
        onChange={(content, _delta, source) => {
          // Only propagate user-initiated edits. Programmatic ('api'/'silent')
          // content sets on mount/prop-change would otherwise echo back into
          // parent state and, when the value doesn't round-trip identically,
          // trigger an infinite render loop (Maximum update depth exceeded).
          if (source === 'user') onChange?.(content)
        }}
        defaultValue={defaultValue}
        placeholder='Write something awesome...'
        modules={modules}
        formats={formats}
        readOnly={readOnly}
      />
    </div>
  )
}
)

MessageEditor.displayName = 'MessageEditor'

export default MessageEditor
